import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from "react";
import { cn } from "../lib/cn";
import { Spinner } from "./Spinner";

export const buttonVariants = cva(
  [
    "press relative inline-flex shrink-0 select-none items-center justify-center gap-1.5",
    "whitespace-nowrap rounded-pill font-medium text-ui",
    "disabled:pointer-events-none disabled:opacity-45",
    "[&_svg]:size-4 [&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        primary:
          "bg-accent text-on-accent shadow-tinted hover:bg-accent-hover active:bg-accent-pressed",
        secondary: "bg-surface-3 text-ink shadow-control hover:bg-surface-4",
        ghost: "text-ink-2 hover:bg-fill-2 hover:text-ink",
        tinted: "bg-accent-soft text-accent-ink hover:bg-accent-soft-hover",
        caution:
          "bg-caution text-caution-ink shadow-[inset_0_0_0_1px_var(--vd-caution-ring)] hover:bg-caution-hover",
        contrast: "bg-contrast text-on-contrast shadow-control hover:bg-contrast-hover",
        danger: "bg-danger text-on-danger shadow-tinted hover:bg-danger-hover",
        answer: "bg-answer text-white shadow-tinted hover:bg-answer-hover",
      },
      size: {
        sm: "h-7 px-2.5 text-caption [&_svg]:size-3.5",
        md: "h-8 px-3",
        lg: "h-9 px-4",
        /** Focus's Start/Pause, the largest control in its transport row. */
        xl: "h-[56px] gap-2 px-6 text-body [&_svg]:size-5",
      },
    },
    defaultVariants: { variant: "secondary", size: "md" },
  },
);

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>["variant"]>;

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render the child element (for example a router link) with button styling. */
  asChild?: boolean;
  /** Shows a spinner in place of the leading icon and disables the button. */
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

/**
 * Pill button. One primary per view. Secondary is the default, ghost is for toolbars,
 * danger is for destructive confirmation, answer is for placing or picking up a call (and the
 * dialer's Start), caution is the running dialer's Pause (solid amber).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    asChild,
    loading = false,
    leadingIcon,
    trailingIcon,
    disabled,
    children,
    type,
    ...props
  },
  ref,
) {
  if (asChild) {
    return (
      <Slot.Root ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props}>
        {children}
      </Slot.Root>
    );
  }
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    >
      {loading ? <Spinner label="" /> : leadingIcon}
      {children}
      {trailingIcon}
    </button>
  );
});
